"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Deep Discovery",
  description: "We begin by auditing your current digital footprint. We identify the friction points between your brand's reality and its public perception, stripping away the noise to find your core signal.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/discovery_audit.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/discovery_audit.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/discovery_audit.webp-wvc-sizes",
  alt: "Minimalist workspace with architectural sketches"
}, {
  number: "02",
  title: "Strategic Positioning",
  description: "We define your unique market stance. By focusing on high-intent messaging, we ensure your brand doesn't just participate in the conversation, but commands it with absolute clarity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/strategic_positioning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/strategic_positioning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/strategic_positioning.png-wvc-sizes",
  alt: "Abstract geometric shapes representing brand strategy"
}, {
  number: "03",
  title: "Content Architecture",
  description: "We build the framework for your narrative. Every piece of content is engineered to serve a specific purpose, ensuring your audience finds exactly what they need to trust your expertise.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_architecture.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_architecture.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/content_architecture.webp-wvc-sizes",
  alt: "Clean layout design on a drafting table"
}, {
  number: "04",
  title: "Signal Amplification",
  description: "We deploy your message across the right channels. We avoid the scattergun approach, focusing instead on high-impact touchpoints that convert interest into genuine, long-term relationships.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/signal_amplification.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/signal_amplification.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/signal_amplification.webp-wvc-sizes",
  alt: "Focused light beam on a textured surface"
}, {
  number: "05",
  title: "Performance Refinement",
  description: "Data is our compass, not our master. We analyze engagement patterns to refine your messaging, ensuring your brand remains sharp, relevant, and consistently authoritative over time.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/performance_refinement.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/performance_refinement.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/performance_refinement.webp-wvc-sizes",
  alt: "Minimalist data visualization on white paper"
}, {
  number: "06",
  title: "Sustainable Growth",
  description: "We transition from launch to legacy. Our final phase ensures your brand is self-sustaining, equipped with the systems and strategy to grow without constant intervention.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/sustainable_growth.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/sustainable_growth.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722464/img/sustainable_growth.png-wvc-sizes",
  alt: "Growth chart rendered in minimalist style"
}];
function ProcessStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  const isReversed = index % 2 === 1;
  return <div data-index={index} ref={ref} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"} overflow-hidden`}>
        <div className="relative aspect-[3/4] overflow-hidden">
          <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-[1400ms] ease-out ${isVisible ? "scale-100" : "scale-105"}`} />
        </div>
      </div>

      <div className={`lg:col-span-6 ${isReversed ? "lg:order-1 lg:pr-8" : "lg:order-2 lg:pl-8"}`}>
        <div aria-hidden="true" className="font-default leading-none select-none text-[120px] sm:text-[150px] lg:text-[180px] tracking-widest" style={{
        WebkitTextStroke: "1px hsl(0 0% 5%)",
        color: "transparent"
      }}>
          {step.number}
        </div>

        <div className="mt-4 lg:mt-2 max-w-md">
          <div className="h-px w-12 bg-accent-signal mb-6" />
          <h3 className="font-default font-medium text-3xl sm:text-4xl text-foreground leading-tight tracking-normal">
            {step.title}
          </h3>
          <p className="mt-5 font-default text-base text-ink-muted leading-relaxed tracking-normal">
            {step.description}
          </p>
        </div>
      </div>
    </div>;
}
export default function Methodology() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section data-section-id="4350" id="the-methodology" className="relative bg-background text-foreground py-24 sm:py-32 lg:py-40 px-6 sm:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={intro.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 mb-24 lg:mb-32 transition-all duration-1000 ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs sm:text-sm uppercase tracking-[0.25em] text-accent-signal mb-8">
              MD-01 &mdash; The Methodology
            </p>
            <h2 className="font-default font-medium text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] tracking-normal text-foreground">
              <span className="block">Disciplined</span>
              <span className="block text-right italic">Creative Strategy.</span>
            </h2>
          </div>

          <div className="lg:col-span-8 lg:col-start-5 mt-10 lg:mt-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 max-w-2xl">
              <p className="font-default text-base text-ink-muted leading-relaxed tracking-normal">
                Six phases, one singular focus. Every project follows a rigorous
                path designed to strip away the unnecessary and amplify your
                brand's true voice.
              </p>
              <p className="font-default text-base text-ink-muted leading-relaxed tracking-normal">
                We don't believe in shortcuts. We believe in the quiet power of
                intentionality. Your brand deserves a strategy that is as
                thoughtful as the work you've built.
              </p>
            </div>
          </div>
        </div>

        <div className="relative">
          <div aria-hidden="true" className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-border-hairline" />

          <div className="space-y-24 lg:space-y-40 relative">
            {steps.map((step, i) => <ProcessStep key={step.number} step={step} index={i} />)}
          </div>
        </div>

        <div ref={closing.ref} className={`mt-24 lg:mt-40 text-center transition-all duration-1000 ${closing.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="h-px w-16 bg-accent-signal mx-auto mb-10" />
          <p className="font-default italic text-3xl sm:text-4xl lg:text-5xl leading-tight text-foreground max-w-3xl mx-auto tracking-normal">
            The internet is waiting for your signal.
          </p>
        </div>
      </div>
    </section>;
}